import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ZoButton } from '@zonofi/zo-components/input/zo-button/zo-button';
import { ZoInputText } from '../zo-input-text-temp/zo-input-text-temp';
import './without-gstin.scss';
import { SVGS } from '../assets';
import { SignUpContainer } from '../sign-up-container/sign-up-container';
import { SellerDetails } from '@zonofi/zo-types';
import { useEventTracking, JTBDEventType } from '@zonofi/seller-rtk-store';
import { CustomSvg } from '@zonofi/gandalf-utilities';
import { SwipeableDrawer } from '../swipeable-drawer/swipeable-drawer';
import { CountriesList } from '../countries-list/countries-list';
import { CountryObject } from '@zonofi/i18n';

interface WithoutGstinProps {
  goBack: () => void;
  setShowContactDetails: () => void;
  sellerDetails: SellerDetails;
  onUpdateValue: (key: string, value: string | CountryObject) => void;
  navigateToBusinessCategory: () => void;
  validateGST: () => void;
}

export const WithoutGstin: React.FC<WithoutGstinProps> = ({
  goBack,
  setShowContactDetails,
  sellerDetails,
  onUpdateValue,
  navigateToBusinessCategory,
  validateGST,
}) => {
  const countryRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [isInternational, setIsInternational] = useState<boolean>(false);
  const [countryDrawerOpen, setCountryDrawerOpen] = useState<boolean>(false);
  const { trackEvent } = useEventTracking();
  const [errors, setErrors] = useState<{
    companyName?: string;
    pinCode?: string;
    email?: string;
  }>({});
  const validateFields = useCallback(() => {
    const newErrors: {
      companyName?: string;
      pinCode?: string;
      contactName?: string;
      email?: string;
    } = {};

    if (!sellerDetails?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!sellerDetails?.contactName?.trim()) {
      newErrors.contactName = 'Contact name is required';
    }

    // Email is mandatory when it wasn't already captured during sign-up.
    if (!sellerDetails?.isEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!sellerDetails?.emailId?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!emailRegex.test(sellerDetails.emailId.trim())) {
        newErrors.email = 'Enter a valid email';
      }
    }

    // if (sellerDetails?.pinCode && sellerDetails.pinCode.length !== 6) {
    //   newErrors.pinCode = 'Pin Code must be 6 digits';
    // }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [sellerDetails]);

  const clickOnInternationalCustomer = () => {
    setIsInternational(true);
    onUpdateValue('selectedCountry', {
      name: 'India',
      flag: '🇮🇳',
      code: 'IN',
      dial_code: '+91',
      currency_code: 'INR',
      time_zones: ['Asia/Kolkata'],
    });
  };

  const openCountriesDrawer = () => {
    setCountryDrawerOpen(true);
  };

  const closeCountriesDrawer = () => {
    setCountryDrawerOpen(false);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (validateFields()) {
          // setShowContactDetails();
          trackEvent(JTBDEventType.SIGNUP_COMPANY_DETAILS_NO_GSTIN, {
            ...sellerDetails,
          });
          navigateToBusinessCategory();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [validateFields]);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    // Set initial viewport width
    setViewportWidth(window.innerWidth);

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const horizontalLayout = viewportWidth > 600;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const anchorEl = countryRef.current;
      const drawerEl = document.querySelector('.countries-list'); // Adjust selector if needed

      if (
        countryDrawerOpen &&
        anchorEl &&
        !anchorEl.contains(event.target as Node) &&
        drawerEl &&
        !drawerEl.contains(event.target as Node)
      ) {
        closeCountriesDrawer();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [countryDrawerOpen]);

  console.log('sellerDetails', sellerDetails);
  return (
    <SignUpContainer customClass="company-details-screen-wrapper custom">
      <div className="without_gstin">
        <div className="without_gstin_header">
          <h2 className="primary-font-semibold seller-font-size-heading">
            Company Details
          </h2>
          <span className="primary-font-regular seller-font-size-sub-heading sub-heading sub-heading">
            Your registered company information as per GST records.
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: horizontalLayout ? 'row' : 'column',
            gap: 32,
          }}
        >
          <div
            style={{
              flex: horizontalLayout ? 1 : 'auto',
            }}
            className="without-gst-form-wrapper"
          >
            <div className="detailsCompanyDetails">
              <div className="detailstop_sec">
                <div className="detailsTrade_sec_wrapper">
                  <CustomSvg
                    svg={SVGS.CompanyNameIconComponent}
                    height={20}
                    width={20}
                    colors={{
                      fill: '#8C8C8C',
                    }}
                    className="GstinIcon"
                  />
                  <div className="detailsTrade_sec">
                    <ZoInputText
                      title="Company Name"
                      placeholder="Enter Company Name"
                      focusInput={true}
                      required={true}
                      value={sellerDetails?.companyName}
                      onBlur={() => {}}
                      inputFieldProps={{
                        value: sellerDetails?.companyName,
                        onChange: (e: { target: { value: string } }) =>
                          onUpdateValue('companyName', e.target.value),
                      }}
                      name={'companyName'}
                      error={(errors?.companyName?.length || 0) > 0}
                    />
                    {(errors?.companyName?.length || 0) > 0 && (
                      <div className="gst-err-msg primary-font-regular">
                        <img
                          src={SVGS.ErrorIcon}
                          width={16}
                          height={16}
                          className="ErrorIcon"
                        />
                        {errors?.companyName}{' '}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="detailsTrade_sec_wrapper">
              <CustomSvg
                svg={SVGS.UserIconComponent}
                height={20}
                width={20}
                colors={{
                  fill: '#8C8C8C',
                }}
                className="GstinIcon"
              />
              <div className="detailsTrade_sec">
                <ZoInputText
                  title="Contact Name"
                  placeholder="Enter Contact Name"
                  value={sellerDetails?.contactName}
                  required={true}
                  onBlur={() => {}}
                  inputFieldProps={{
                    value: sellerDetails?.contactName,
                    onChange: (e: { target: { value: string } }) => {
                      const input = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                      const formatted = input
                        .split(' ')
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase()
                        )
                        .join(' ');

                      onUpdateValue('contactName', formatted);
                    },
                  }}
                  name={'contactName'}
                  // extraTitle={{}
                  // <ZoTooltip
                  //   title={
                  //     'Example: Registered name is “abc” but the brand name can be “xyz”.'
                  //   }
                  //   arrowDirection="right"
                  // >
                  //   <img src={SVGS.InfoGreyFillIcon} />
                  // </ZoTooltip>
                  // }
                />
              </div>
            </div>
            {!sellerDetails?.isEmail && (
              <div className="detailsTrade_sec_wrapper">
                <CustomSvg
                  svg={SVGS.EmailIconComponent}
                  height={20}
                  width={20}
                  colors={{
                    fill: '#8C8C8C',
                  }}
                  className="GstinIcon"
                />
                <div className="detailsTrade_sec">
                  <ZoInputText
                    title="Email"
                    placeholder="Enter Email"
                    value={sellerDetails?.emailId}
                    required={true}
                    onBlur={() => {}}
                    inputFieldProps={{
                      value: sellerDetails?.emailId,
                      onChange: (e: { target: { value: string } }) =>
                        onUpdateValue('emailId', e.target.value),
                    }}
                    name={'emailId'}
                    error={(errors?.email?.length || 0) > 0}
                  />
                  {(errors?.email?.length || 0) > 0 && (
                    <div className="gst-err-msg primary-font-regular">
                      <img
                        src={SVGS.ErrorIcon}
                        width={16}
                        height={16}
                        className="ErrorIcon"
                      />
                      {errors?.email}{' '}
                    </div>
                  )}
                </div>
              </div>
            )}
            {sellerDetails.isEmail ? (
              <div className="detailsTrade_sec_wrapper">
                <span className="PhoneIcon">
                  <CustomSvg
                    svg={SVGS.EmailIconComponent}
                    height={20}
                    width={20}
                    colors={{
                      fill: '#8C8C8C',
                    }}
                    className="GstinIcon"
                  />
                </span>
                <div className="contactInfo">
                  <label>Email</label>
                  <p className="primary-font-regular">
                    {sellerDetails?.emailId}{' '}
                    <span className="icon" style={{ marginLeft: '2px' }}>
                      <img
                        src={SVGS.SuccessTickDark}
                        width={20}
                        height={20}
                        className="SuccessTick"
                        alt=""
                      />
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="detailsTrade_sec_wrapper">
                <span className="PhoneIcon">
                  <CustomSvg
                    svg={SVGS.PhoneIconComponent}
                    height={20}
                    width={20}
                    colors={{
                      fill: '#8C8C8C',
                    }}
                    className="GstinIcon"
                  />
                </span>
                <div className="contactInfo">
                  <label>Mobile Number</label>
                  <p className="primary-font-regular">
                    {sellerDetails?.dial_code} {sellerDetails?.phoneNumber}{' '}
                    <span
                      className="icon"
                      style={{ marginLeft: '2px', lineHeight: '28px' }}
                    >
                      <img
                        src={SVGS.SuccessTickDark}
                        width={16}
                        height={16}
                        className="SuccessTick"
                        alt=""
                      />
                    </span>
                  </p>
                </div>
              </div>
            )}

            {isInternational && (
              <div className="detailsTrade_sec_wrapper">
                <span className="PhoneIcon">
                  <SVGS.GlobeIconComponent
                    width={20}
                    height={20}
                    className="GstinIcon"
                  />
                </span>
                <div className="countryInfo">
                  <label>Country</label>
                  <div
                    className="country-wrapper"
                    onClick={openCountriesDrawer}
                    ref={countryRef}
                  >
                    <div className="country-container">
                      <span className="country-flag">
                        {sellerDetails?.selectedCountry?.flag}
                      </span>
                      <span className="country-name primary-font-regular">
                        {sellerDetails?.selectedCountry?.name}
                      </span>
                    </div>
                    <img
                      src={SVGS.SelectArrowDown}
                      alt=""
                      width={20}
                      height={20}
                    />
                  </div>

                  <span className="prices-info primary-font-regular">
                    Prices will be shown in the selected country’s currency.
                  </span>
                </div>
                <SwipeableDrawer
                  open={countryDrawerOpen}
                  anchorRef={countryRef}
                  openDrawer={openCountriesDrawer}
                  closeDrawer={closeCountriesDrawer}
                >
                  <CountriesList
                    closeCountriesDrawer={closeCountriesDrawer}
                    selectedCountry={sellerDetails?.selectedCountry}
                    setSelectedCountry={(selectedCountry) => {
                      onUpdateValue('selectedCountry', selectedCountry);
                    }}
                  />
                </SwipeableDrawer>
              </div>
            )}

            {!isInternational && (
              <div className="international-info primary-font-regular">
                If you’re an international customer, please{' '}
                <span
                  className="international-info-btn"
                  onClick={clickOnInternationalCustomer}
                >
                  click here to onboard
                </span>
                .
              </div>
            )}

            {!horizontalLayout && <img src={SVGS.VerticalDivider} alt="" />}

            {!horizontalLayout && (
              <div
                style={{
                  borderRadius: '8px',
                  background: '#CEEAF9',
                  display: 'flex',
                  padding: '16px',
                  marginTop: 16,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  alignSelf: 'stretch',
                }}
              >
                <div
                  style={{
                    color: '#262626',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '22px',
                  }}
                >
                  Quick Setup with GSTIN (Optional)
                </div>
                <div
                  style={{
                    color: '#595959',
                    textAlign: 'left',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                >
                  Have a GSTIN? Enter it below and we'll instantly fill your
                  company details - saving you time and ensuring accuracy.
                </div>
                <div style={{ width: '100%' }}>
                  <ZoInputText
                    title={'GST Number'}
                    name={'gstin'}
                    required={false}
                    value={sellerDetails?.gstin}
                    onBlur={() => {}}
                    focusInput={false}
                    inputFieldProps={{
                      name: 'gstin',
                      placeholder: 'Provide your GST Number',
                      value: sellerDetails?.gstin,
                      onChange: (e: { target: { value: string } }) => {
                        const regex = /^[0-9A-Za-z]{0,15}$/;
                        if (regex.test(e.target.value)) {
                          onUpdateValue('gstin', e.target.value?.toUpperCase());
                        }
                      },
                      maxLength: 15,
                    }}
                    // error={!!gstErrorMessage}
                    endAdornment={
                      <span
                        className="text-gray-500"
                        style={{
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#8c8c8c',
                        }}
                      >
                        {sellerDetails?.gstin?.length}/15{' '}
                      </span>
                    }
                  />
                  {/* {gstErrorMessage && (
                <div className="gstErrorMsg">
                  <img
                    src={SVGS.ErrorIcon}
                    width={16}
                    height={16}
                    className="ErrorIcon"
                  />
                  {gstErrorMessage}
                </div>
              )} */}
                </div>
                <div
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #32A7E8',
                    display: 'flex',
                    padding: '12px 16px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    alignSelf: 'stretch',
                    color: '#32A7E8',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    validateGST();
                  }}
                >
                  Fetch Details
                </div>
              </div>
            )}

            {/* <div className="detailAddress_sec">
            <div className="headingSec">
              <h3 className="primary-font-semibold">Company Address</h3>
            </div>
            <div className="editableAddressSectionWrapper">
              <CustomSvg
                svg={SVGS.LocationIconComponent}
                height={20}
                width={20}
                colors={{
                  fill: '#8C8C8C',
                }}
                className="GstinIcon"
              />
              <div className="editableAddressSection">
                <div className="editaddres_top">
                  <ZoInputText
                    placeholder="Enter Address"
                    value={sellerDetails?.address}
                    title="Address"
                    onBlur={() => {}}
                    inputFieldProps={{
                      value: sellerDetails?.address,
                      onChange: (e: { target: { value: string } }) =>
                        onUpdateValue('address', e.target.value),
                    }}
                    name="address"
                  />
                </div>
                <div className="editaddress_bottom">
                  <ZoInputText
                    name="city"
                    title="City"
                    placeholder="Enter City"
                    value={sellerDetails?.city}
                    onBlur={() => {}}
                    inputFieldProps={{
                      value: sellerDetails?.city,
                      onChange: (e: { target: { value: string } }) => {
                        const value = e.target.value.replace(
                          /[^a-zA-Z\s]/g,
                          ''
                        );
                        onUpdateValue('city', value);
                      },
                    }}
                  />
                  <ZoSelect
                    title="State"
                    value={sellerDetails?.state}
                    placeholder="Select State"
                    onSelectValue={(val: string) => onUpdateValue('state', val)}
                    options={states}
                  />
                  <div className="pincode_sec">
                    <ZoInputText
                      title="Pin Code"
                      placeholder="000000"
                      value={sellerDetails?.pinCode}
                      onBlur={() => {}}
                      inputFieldProps={{
                        value: sellerDetails?.pinCode,
                        onChange: (e: { target: { value: string } }) => {
                          const newValue = e.target.value;
                          if (/^\d{0,6}$/.test(newValue)) {
                            onUpdateValue('pinCode', newValue);
                          }
                        },
                      }}
                      endAdornment={
                        <span
                          className="text-gray-500"
                          style={{
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#8c8c8c',
                          }}
                        >
                          {sellerDetails?.pinCode?.length}/6
                        </span>
                      }
                      name="pincode"
                      error={(errors?.pinCode?.length || 0) > 0}
                    />
                    {(errors?.pinCode?.length || 0) > 0 && (
                      <div className="gst-err-msg primary-font-regular">
                        <img
                          src={SVGS.ErrorIcon}
                          width={16}
                          height={16}
                          className="ErrorIcon"
                        />
                        {errors?.pinCode}{' '}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div> */}
          </div>
          {horizontalLayout && <img src={SVGS.HorizontalDivider} />}
          {horizontalLayout && (
            <div
              style={{
                borderRadius: '8px',
                background: '#CEEAF9',
                display: 'flex',
                padding: '16px',
                marginTop: 16,
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '8px',
                alignSelf: 'stretch',
                flex: horizontalLayout ? 1 : 'auto',
              }}
            >
              <div
                style={{
                  color: '#262626',
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '22px',
                }}
              >
                Quick Setup with GSTIN (Optional)
              </div>
              <div
                style={{
                  color: '#595959',
                  textAlign: 'left',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                Have a GSTIN? Enter it below and we'll instantly fill your
                company details - saving you time and ensuring accuracy.
              </div>
              <div style={{ width: '100%' }}>
                <ZoInputText
                  title={'GST Number'}
                  name={'gstin'}
                  required={false}
                  value={sellerDetails?.gstin}
                  onBlur={() => {}}
                  focusInput={false}
                  inputFieldProps={{
                    name: 'gstin',
                    placeholder: 'Provide your GST Number',
                    value: sellerDetails?.gstin,
                    onChange: (e: { target: { value: string } }) => {
                      const regex = /^[0-9A-Za-z]{0,15}$/;
                      if (regex.test(e.target.value)) {
                        onUpdateValue('gstin', e.target.value?.toUpperCase());
                      }
                    },
                    maxLength: 15,
                  }}
                  // error={!!gstErrorMessage}
                  endAdornment={
                    <span
                      className="text-gray-500"
                      style={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#8c8c8c',
                      }}
                    >
                      {sellerDetails?.gstin?.length}/15{' '}
                    </span>
                  }
                />
                {/* {gstErrorMessage && (
                <div className="gstErrorMsg">
                  <img
                    src={SVGS.ErrorIcon}
                    width={16}
                    height={16}
                    className="ErrorIcon"
                  />
                  {gstErrorMessage}
                </div>
              )} */}
              </div>
              <div
                style={{
                  borderRadius: '4px',
                  border: '1px solid #32A7E8',
                  display: 'flex',
                  padding: '12px 16px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  alignSelf: 'stretch',
                  color: '#32A7E8',
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  validateGST();
                }}
              >
                Fetch Details
              </div>
            </div>
          )}
        </div>
        <div className="details_bottomSec">
          <ZoButton
            label="Continue"
            style={{
              color: '#ffffff',
            }}
            endIcon={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <SVGS.RightArrowComponent
                  // height={20}
                  // width={20}
                  fill={'#FFFFFF'}
                  stroke={'#FFFFFF'}
                  // colors={{
                  //   stroke:
                  //     isEmpty(sellerDetails?.companyName) ||
                  //     isEmpty(sellerDetails?.contactName)
                  //       ? '#454545'
                  //       : '#FFFFFF',
                  //   fill:
                  //     isEmpty(sellerDetails?.companyName) ||
                  //     isEmpty(sellerDetails?.contactName)
                  //       ? '#454545'
                  //       : '#FFFFFF',
                  // }}
                  className="GstinIcon"
                />
                {/* <CustomSvg
                  svg={SVGS.RightArrowComponent}
                  height={20}
                  width={20}
                  colors={{
                    stroke: isEmpty(sellerDetails?.companyName)
                      ? '#8C8C8C'
                      : '#FFFFFF',
                    fill: isEmpty(sellerDetails?.companyName)
                      ? '#8C8C8C'
                      : '#FFFFFF',
                  }}
                  className="GstinIcon"
                /> */}
                {/* <img
                  src={SVGS.ArrowRight}
                  width={20}
                  height={20}
                  className="ArrowRight"
                /> */}
              </span>
            }
            // onClick={() => console.log('Continue with company details')}
            onClick={() => {
              if (validateFields()) {
                trackEvent(JTBDEventType.SIGNUP_COMPANY_DETAILS_NO_GSTIN, {
                  ...sellerDetails,
                });
                navigateToBusinessCategory();
                // setShowContactDetails();
              }
            }}
            className="sign-up-now-btn"
          />
        </div>
      </div>
    </SignUpContainer>
  );
};
